import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";

export interface DashboardFilters {
  regionId: string;
  organizationId: string;
  siteId: string;
}

interface DashboardFiltersProps {
  filters: DashboardFilters;
  onFiltersChange: (filters: DashboardFilters) => void;
}

const DashboardFiltersComponent: React.FC<DashboardFiltersProps> = ({ 
  filters, 
  onFiltersChange 
}) => {
  // Fetch regions
  const { data: regions } = useQuery({
    queryKey: ["regions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("regions")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch organizations based on selected region
  const { data: organizations } = useQuery({
    queryKey: ["organizations", filters.regionId],
    queryFn: async () => {
      let query = supabase
        .from("organizations")
        .select("*")
        .order("name");
      
      if (filters.regionId !== "all") {
        query = query.eq('region_id', filters.regionId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: true
  });

  // Fetch sites based on selected organization/region
  const { data: sites } = useQuery({
    queryKey: ["sites", filters.regionId, filters.organizationId],
    queryFn: async () => {
      // First, get all organizations if we're filtering by region only
      let orgIds: string[] = [];
      
      if (filters.regionId !== "all" && filters.organizationId === "all") {
        const { data: orgs } = await supabase
          .from("organizations")
          .select("id")
          .eq('region_id', filters.regionId);
          
        if (orgs) {
          orgIds = orgs.map(org => org.id);
        }
      }
      
      // Build the main query for sites
      let query = supabase
        .from("sites")
        .select("*")
        .order('name');
      
      // Apply filters
      if (filters.organizationId !== "all") {
        // Filter by specific organization
        query = query.eq('organization_id', filters.organizationId);
      } else if (filters.regionId !== "all" && orgIds.length > 0) {
        // Filter by all organizations in the selected region
        query = query.in('organization_id', orgIds);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: true
  });

  const handleRegionChange = (regionId: string) => {
    onFiltersChange({
      regionId,
      organizationId: "all",
      siteId: "all"
    });
  };

  const handleOrganizationChange = (organizationId: string) => {
    onFiltersChange({
      ...filters,
      organizationId,
      siteId: "all"
    });
  };

  const handleSiteChange = (siteId: string) => {
    onFiltersChange({
      ...filters,
      siteId
    });
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Filter className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm text-muted-foreground">Filter by:</span>
      
      <Select value={filters.regionId} onValueChange={handleRegionChange}>
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="All Regions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Regions</SelectItem>
          {regions?.map((region) => (
            <SelectItem key={region.id} value={region.id}>
              {region.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select 
        value={filters.organizationId} 
        onValueChange={handleOrganizationChange}
        disabled={!organizations || organizations.length === 0}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="All Organizations" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Organizations</SelectItem>
          {organizations?.map((org) => (
            <SelectItem key={org.id} value={org.id}>
              {org.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select 
        value={filters.siteId} 
        onValueChange={handleSiteChange}
        disabled={!sites || sites.length === 0}
      >
        <SelectTrigger className="w-[140px]">
          <SelectValue placeholder="All Sites" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Sites</SelectItem>
          {sites?.map((site) => (
            <SelectItem key={site.id} value={site.id}>
              {site.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DashboardFiltersComponent; 